import { ThemedIcon } from "@src/common/ThemedIcon";
import type { ProviderPhoto as ProviderPhotoData } from "@src/interfaces/provider-photo.interface";
import { getProviderPhotoSrc } from "@src/utils/provider-photo-utils";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import type { CSSProperties, FC } from "react";

interface ProviderPhotoProps {
  providerPhoto?: ProviderPhotoData;
  altText: string;
  fallbackIconSize: number;
  fallbackIconStyle?: CSSProperties;
  imageStyle?: CSSProperties;
  imageTestId?: string;
  isLoading?: boolean;
  loadingLabel?: string;
}

export const ProviderPhoto: FC<ProviderPhotoProps> = ({
  providerPhoto,
  altText,
  fallbackIconSize,
  fallbackIconStyle,
  imageStyle,
  imageTestId = "provider-photo-image",
  isLoading = false,
  loadingLabel = "Loading provider photo",
}) => {
  const providerPhotoSrc = getProviderPhotoSrc(providerPhoto);
  const fallbackWidth = fallbackIconStyle?.width || `${fallbackIconSize}px`;
  const fallbackHeight = fallbackIconStyle?.height || `${fallbackIconSize}px`;
  const content = providerPhotoSrc ? (
    <img
      src={providerPhotoSrc}
      alt={altText}
      data-testid={imageTestId}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        ...imageStyle,
      }}
    />
  ) : (
    <ThemedIcon
      themeIconName="mental_health"
      themedIconStyle={fallbackIconStyle}
      themedImgProps={{ "aria-hidden": true }}
      iconBrandProps={{
        icon: "mental_health",
        brand: "uhc",
        size: fallbackIconSize,
      }}
    />
  );

  return (
    <div
      data-testid="test-id-provider-photo-container"
      style={{
        position: "relative",
        width: providerPhotoSrc ? "100%" : fallbackWidth,
        height: providerPhotoSrc ? "100%" : fallbackHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {content}
      {isLoading && (
        <div
          data-testid="test-id-provider-photo-loading-overlay"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingSpinner
            size="lg"
            ariaLoadingLabel={loadingLabel}
            isLoading={true}
            color="$app.color.brand.primary"
          />
        </div>
      )}
    </div>
  );
};
